import { useRef, useState } from 'react'
import axios from 'axios'
import type { AgentOutputItem } from '../agent'
import { API_BASE_URL } from '../constants/system'

interface OutputSearchResponse {
  outputs?: AgentOutputItem[]
  count?: number
  notice?: string
}

/** Where an output opens: a route name and its params, for `router.navigate`. */
export interface OutputDestination {
  name: 'agent-session' | 'tables' | 'dashboard-view'
  params: Record<string, string>
}

/**
 * Where clicking a past-work output goes (#1649). An artifact opens its chat
 * with the artifact's preview (`?artifact=`); a table opens Tables on it; a
 * dashboard the agent made opens in DataViz (#1886). The Tables route takes
 * the blob path without `.cbt` (`useTablePath` adds it).
 */
export function outputDestination(output: AgentOutputItem, fallbackCustomer: string): OutputDestination {
  const customer = output.customer || fallbackCustomer
  if (output.kind === 'dashboard' && output.dashboard_id) {
    return { name: 'dashboard-view', params: { customer, dashboardId: output.dashboard_id } }
  }
  if (output.table_path && output.job) {
    return { name: 'tables', params: { customer, job: output.job, table: output.table_path.replace(/\.cbt$/i, '') } }
  }
  const params: Record<string, string> = { customer, sessionId: output.session_id }
  if (output.artifact_id) params.artifact = output.artifact_id
  return { name: 'agent-session', params }
}

/**
 * Past-work search for the History tab (#1649): the outputs past chats made
 * that match the search, from `GET /agent/:customer/outputs`. Runs beside the
 * chat search, with the same user filter ('me' = the caller, '*' = everyone,
 * else that person). A reply to a search that is no longer the applied one is
 * dropped, so a slow answer never overwrites a newer one.
 */
export function useOutputSearch(customer: string) {
  const [outputs, setOutputs] = useState<AgentOutputItem[]>([])
  const [outputsNotice, setOutputsNotice] = useState('')
  const [isSearchingOutputs, setIsSearchingOutputs] = useState(false)
  // The query and user filter of the search the outputs belong to.
  const appliedRef = useRef('')

  const handleClearOutputs = () => {
    appliedRef.current = ''
    setOutputs([])
    setOutputsNotice('')
    setIsSearchingOutputs(false)
  }

  const handleSearchOutputs = async (query: string, userFilter: string) => {
    const q = query.trim()
    if (!q || !customer) {
      handleClearOutputs()
      return
    }
    const key = `${q}\u0000${userFilter}`
    appliedRef.current = key
    const params = new URLSearchParams({ q })
    if (userFilter === '*') params.set('user_email', '*')
    else if (userFilter && userFilter !== 'me') params.set('user_email', userFilter)
    setIsSearchingOutputs(true)
    try {
      const resp = await axios.get<OutputSearchResponse>(
        `${API_BASE_URL}/agent/${encodeURIComponent(customer)}/outputs?${params.toString()}`,
      )
      if (appliedRef.current !== key) return
      setOutputs(resp.data.outputs || [])
      setOutputsNotice(resp.data.notice || '')
    } catch (err) {
      if (appliedRef.current !== key) return
      console.error('Failed to search outputs:', err)
      setOutputs([])
      setOutputsNotice('')
    } finally {
      if (appliedRef.current === key) setIsSearchingOutputs(false)
    }
  }

  return { outputs, outputsNotice, isSearchingOutputs, handleSearchOutputs, handleClearOutputs }
}

export default useOutputSearch
